/**
 * How the wizard reads a refusal from the data-source API.
 *
 * The controller answers a conflict as 409 with `{error, message}`, and the `error` word is what
 * separates four very different situations that all arrive as the same status code
 * (DataSourcesController.cs:48-58). Discriminating on it is what lets the wizard put a duplicate
 * name back on the name field instead of throwing a generic toast at someone who then has to
 * guess which of five steps to fix.
 */

/** The `error` words a 409 can carry. Copied verbatim from the controller's constants. */
export const CompositeSourceConflict = Object.freeze({
  NameTaken: 'nameConflict',
  NotDraft: 'definitionNotDraft',
  NotPublished: 'definitionNotPublished',
  RowVersionMismatch: 'rowVersionMismatch',
  ViewNameCollision: 'viewNameCollision',
  OutputContractConflict: 'outputContractConflict',
});

/**
 * What the wizard says when a 409 comes back. Deliberately actionable — each one names the move
 * that clears it, because "conflict" on its own tells the author nothing they can act on.
 */
const conflictCopy = Object.freeze({
  [CompositeSourceConflict.NameTaken]: 'That name is already used by another composite source for this target. Choose a different name.',
  [CompositeSourceConflict.NotDraft]: 'This source changed lifecycle state while you were editing. Your local changes are still here. Close and reopen the source to load its current state, then try again.',
  [CompositeSourceConflict.NotPublished]: 'This source is now a draft. Your local changes are still here. Close and reopen it, then save the draft explicitly.',
  [CompositeSourceConflict.RowVersionMismatch]: 'Someone else changed this source while you were editing. Your local changes are still here. Close and reopen the source to load the latest version before applying them again.',
  [CompositeSourceConflict.ViewNameCollision]: 'The database already holds a view with the name this source would compile to. Report this — it needs an administrator.',
  [CompositeSourceConflict.OutputContractConflict]: 'These changes would remove or change columns from the live source. The current source is still available. Restore the listed columns and their types, then try again.',
});

const conflictTitle = Object.freeze({
  [CompositeSourceConflict.NameTaken]: 'That name is unavailable',
  [CompositeSourceConflict.NotDraft]: 'The source state changed',
  [CompositeSourceConflict.NotPublished]: 'The source state changed',
  [CompositeSourceConflict.RowVersionMismatch]: 'A newer version was saved',
  [CompositeSourceConflict.ViewNameCollision]: 'The compiled view name is unavailable',
  [CompositeSourceConflict.OutputContractConflict]: 'Changes were not applied',
});

/** The `error` word of a 409, or null for any other failure. */
export function conflictCodeOf(err) {
  const response = err && err.response;
  if (!response || response.status !== 409) return null;
  return (response.data && response.data.error) || null;
}

/**
 * The most specific human message available for a failed call, in falling order of usefulness:
 * our copy for a known conflict, then the server's own message, then the caller's fallback.
 *
 * The server's message is preferred over a generic one because the validator answers with the
 * rule that was broken — text written to be read by the person authoring the definition.
 */
export function describeApiError(err, fallback) {
  const code = conflictCodeOf(err);
  if (code && conflictCopy[code]) return conflictCopy[code];

  const data = err && err.response && err.response.data;
  if (data) {
    if (typeof data === 'string' && data.trim()) return data;
    if (data.message) return data.message;
  }

  return fallback;
}

/**
 * Validation failures arrive as 400 with the broken rules intact — `{error, message, errors:
 * [{code, path, message}]}` (DataSourcesController.cs:364-379). Each rule keeps its own code,
 * field path and message precisely so a wizard can point at the offending field; Wave D anchors
 * them to steps, and this returns them as one readable line for the meantime.
 *
 * Returns null when the failure was not a validation one, so callers can fall through.
 */
export function describeValidationFailure(err) {
  const data = err && err.response && err.response.data;
  const errors = data && data.errors;
  if (!Array.isArray(errors) || !errors.length) return null;

  const reasons = errors.map((entry) => entry && entry.message).filter(Boolean);
  return reasons.length ? reasons.join(' ') : null;
}

/** A persistent, actionable failure state for a save/apply/repair attempt. */
export function describeOperationFailure(err, fallback) {
  const code = conflictCodeOf(err);
  const data = err && err.response && err.response.data;

  return {
    code,
    title: (code && conflictTitle[code]) || 'Changes were not saved',
    message: describeValidationFailure(err) || describeApiError(err, fallback),
    columns: code === CompositeSourceConflict.OutputContractConflict
      && data && Array.isArray(data.columns) ? data.columns.filter(Boolean) : [],
  };
}
